import {
  OWNER_SETUP_FEATURE_VERSION,
  type OwnerActivationState,
} from '../redux/ownerActivationSlice';

/**
 * Pure route decision for new-owner setup entry (US1). Consumed by
 * AppNav/AppStack after redux-persist rehydration; never guesses while
 * state is still loading.
 */

export type OwnerSetupEntryDecision =
  /** Rehydration incomplete: render a loading gate, decide nothing. */
  | { kind: 'wait' }
  /** No usable session: normal Auth stack (Login/Register). */
  | { kind: 'auth' }
  /**
   * Owner-bound eligibility exists with a pending session bootstrap: run
   * OwnerSetupSessionBootstrap recovery before exposing Home or Auth.
   */
  | { kind: 'session_recovery' }
  /** Authenticated eligible owner with a non-terminal wizard: enter wizard. */
  | { kind: 'wizard'; entryMode: 'initial' | 'resume' }
  /**
   * Normal app. `requiresIdentityReset` signals that persisted activation
   * state belongs to a different owner and must be reset on entry.
   */
  | { kind: 'app'; requiresIdentityReset: boolean };

export interface OwnerSetupEntryInput {
  /** redux-persist root rehydration completed. */
  rehydrated: boolean;
  /** Employee profile present in Redux AND an access token exists. */
  isAuthenticated: boolean;
  /** Authenticated employee's stable owner id (bp_owner_id), if known. */
  authenticatedOwnerKey: string | null;
  ownerActivation: Pick<
    OwnerActivationState,
    'ownerKey' | 'eligibility' | 'wizard'
  >;
}

export const decideOwnerSetupEntry = (
  input: OwnerSetupEntryInput,
): OwnerSetupEntryDecision => {
  const { rehydrated, isAuthenticated, authenticatedOwnerKey } = input;
  const { ownerKey, eligibility, wizard } = input.ownerActivation;

  if (!rehydrated) {
    return { kind: 'wait' };
  }

  const versionedEligible =
    eligibility.eligible === true &&
    ownerKey !== null &&
    eligibility.featureVersion === OWNER_SETUP_FEATURE_VERSION;

  if (!isAuthenticated) {
    if (versionedEligible && eligibility.sessionBootstrap === 'pending') {
      // Fresh tokens may exist even though Redux auth was interrupted by
      // process death; recovery decides between wizard and Login.
      return { kind: 'session_recovery' };
    }
    // Includes the requires_login envelope: the owner must sign in, and the
    // preserved eligibility resumes on identity match inside Login.
    return { kind: 'auth' };
  }

  const ownerMatches =
    ownerKey !== null &&
    !!authenticatedOwnerKey &&
    ownerKey === authenticatedOwnerKey;

  if (versionedEligible && !ownerMatches) {
    // Different account signed in over another owner's persisted progress.
    return { kind: 'app', requiresIdentityReset: true };
  }

  if (versionedEligible && ownerMatches) {
    if (eligibility.sessionBootstrap === 'pending') {
      return { kind: 'session_recovery' };
    }
    if (wizard.status === 'not_started') {
      return { kind: 'wizard', entryMode: 'initial' };
    }
    if (wizard.status === 'in_progress') {
      return { kind: 'wizard', entryMode: 'resume' };
    }
    // Terminal wizard (completed/skipped): normal Home with tracker.
    return { kind: 'app', requiresIdentityReset: false };
  }

  // Returning owner / employee with no eligibility envelope.
  return {
    kind: 'app',
    requiresIdentityReset:
      ownerKey !== null && !!authenticatedOwnerKey && !ownerMatches,
  };
};
